import { Expense, TripFolder } from "@finway-group/shared/lib/models"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { detachAllFolderContents, syncExpense, unarchiveExpense, unarchiveMultipleExpenses } from "~Shared/store/actions/expense/expenseActions"
import { isFolderAttachableAndDetachable } from "~Shared/utils/folder.utils"
import { ConfirmOption, MultiActionConfirmModal } from "./multiActionConfirm.modal"

interface UnarchiveFolderModalProps {
    folder: TripFolder
    subExpenses: Array<Expense>
    isShowing: boolean
    onCancel: () => void
}

export const UnarchiveFolderModal = ({ folder, isShowing, onCancel, subExpenses }: UnarchiveFolderModalProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const areSubExpensesDetachable = isFolderAttachableAndDetachable(folder)
    const isFolderFilled = subExpenses.length > 0

    const executeUnarchiveFolder = async (scope?: "all" | "folder_only") => {
        const toBeArchivedIds = [folder, ...subExpenses].map((expense) => expense.id)

        try {
            // If the callback sends no parameter and we dont ask the user to choose the scope of tha archive, that means we can just archive the folder itself
            if (!scope && !isFolderFilled) {
                await unarchiveExpense(folder.id)(dispatch)
            } else if (!scope && !areSubExpensesDetachable) {
                await unarchiveMultipleExpenses(toBeArchivedIds)(dispatch)
            } else {
                switch (scope) {
                    case "all":
                        // Archives the folder and the contents
                        await unarchiveMultipleExpenses(toBeArchivedIds)(dispatch)
                        break
                    case "folder_only":
                        // Detach all contents, archive the folder only
                        await detachAllFolderContents(folder.id)(dispatch)
                        await unarchiveExpense(folder.id)(dispatch)
                        break
                }
            }

            NotificationService.send(NotificationTypeEnum.SUCCESS, t(`notification:trip_folder.unarchived.title`), t(`notification:trip_folder.unarchived.message`))
            onCancel()
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            await syncExpense(folder.id)(dispatch)
        }
    }

    const getUnarchiveConfirmActions = (): Array<ConfirmOption> => {
        if (!isFolderFilled || !areSubExpensesDetachable) {
            return []
        }
        return [
            {
                action: "folder_only",
                text: t("confirm:request.unarchive_folder.select.folder_only"),
            },
            {
                action: "all",
                text: t("confirm:request.unarchive_folder.select.all"),
            },
        ]
    }

    const getUnarchiveDescription = () => {
        if (!isFolderFilled) {
            return t("confirm:request.unarchive_folder.message")
        }
        if (!areSubExpensesDetachable) {
            return t("confirm:request.unarchive_folder.all")
        }
        return `${t("confirm:request.unarchive_folder.message")} ${t("confirm:request.unarchive_folder.extra")}`
    }

    return (
        <MultiActionConfirmModal
            isShowing={isShowing}
            onCancel={onCancel}
            onConfirm={executeUnarchiveFolder}
            okButtonText={t("confirm:request.unarchive_folder.confirm")}
            cancelButtonText={t("confirm:request.unarchive_folder.cancel")}
            title={t("confirm:request.unarchive_folder.title")}
            description={getUnarchiveDescription()}
            size="sm"
            confirmOptions={getUnarchiveConfirmActions()}
        />
    )
}
